import type { RouteLocationRaw } from "vue-router"
import type { SchemaField } from "@auger/core"
import { enrichField } from "@auger/core/collections"
import { type AugerField, isSelectField } from "@auger/core/fields"
import { getSchema, getSchemaFields } from "@/stores/schemas"
import { getTypeColor, getTypeIcon } from "@/stores/types"

type RecordStyleSource = { schema_id?: number | null; type?: string | null } | null | undefined

// Icon: record's own schema (looked up by id) → fall through to the type's
// icon (which itself resolves type.schema.icon → hardcoded fallback).
export const getRecordIcon = (r: RecordStyleSource) =>
  getSchema(r?.schema_id)?.icon || getTypeIcon(r?.type)

// Color: same chain as icon.
export const getRecordColor = (r: RecordStyleSource) =>
  getSchema(r?.schema_id)?.color || getTypeColor(r?.type)

/**
 * A change stamp for a record that is half columns and half rows — for keeping
 * two live copies of it (a list row and an open panel) in step.
 *
 * `updated_at` alone is not enough. Reference fields — assignee, project,
 * related_to, tags, attachments — don't live in `records.data`; they're rows in
 * their own tables, and the updated_at trigger stamps the row it fired on. So
 * writing a link bumps `record_links.updated_at` and leaves `records.updated_at`
 * exactly where it was: a watcher on the stamp never sees a reassignment.
 *
 * The signature covers both halves — the stamp for the data columns, and the
 * link set for the fields that are rows. Order-independent, so a refetch that
 * returns the same links in a different order doesn't read as a change.
 */
export const recordSyncSignature = (
  record:
    | {
        id: number
        updated_at?: string | null
        links?: readonly { schema_key?: string | null; linked_id: number }[] | null
      }
    | null
    | undefined
): string | null => {
  if (!record) return null
  const links = (record.links ?? [])
    .map((l) => `${l.schema_key}:${l.linked_id}`)
    .sort()
    .join(",")
  return `${record.id}|${record.updated_at}|${links}`
}

// A status by name: `status` itself, or the `<thing>_status` our schemas use to
// keep several apart on one record (Intro Candidate carries opt_in_status,
// candidate_status and enrichment_status).
const STATUS_KEY = /^status$|_status$/

/**
 * A record's status field — the decision someone makes about it — resolved from
 * ITS OWN schema and enriched, so a caller can hand it straight to a renderer.
 *
 * `key` names the field outright. Without one the schema is read twice, in
 * order:
 *
 *   1. a status TYPE (`review_status` / `task_status` / `deal_status`), which
 *      says what the field is regardless of what it's called;
 *   2. failing that, a plain `select` named like a status — which is how most
 *      of our schemas spell one (`proposal_status`, `sprint_status`,
 *      `objective_status`).
 *
 * Anything else is left alone: `disposition`, `company_health`, `priority` and
 * `seniority` are properties of a record, not decisions about it, and promoting
 * one automatically would put an arbitrary field in a status slot. A schema with
 * neither resolves to null and callers show none.
 */
export const resolveStatusField = (
  schemaId?: number | null,
  key?: string | null
): AugerField | null => {
  if (schemaId == null) return null
  const fields = getSchemaFields(schemaId).filter((f: SchemaField) => !f.isHidden)
  const match = key
    ? fields.find((f: SchemaField) => f.key === key)
    : (fields.find((f: SchemaField) => f.type !== "select" && isSelectField(f)) ??
      fields.find((f: SchemaField) => f.type === "select" && STATUS_KEY.test(f.key)))
  return match ? enrichField(match, { schema_id: schemaId, isEditable: true }) : null
}

// Vue Router route object for a record. Lists use a dedicated route; everything
// else uses the generic record-show route keyed on type + slug.
export const getRecordUrl = (
  record: { type?: string | null; slug?: string | null } | null | undefined
): RouteLocationRaw => {
  if (record?.type === "list") {
    return { name: "list-show", params: { slug: record?.slug || "error" } }
  }
  return {
    name: "record-show",
    params: {
      recordType: record?.type || "error",
      slug: record?.slug || "error",
    },
  }
}
